//#region INTERFACE IAutoOrderFiltersAndTools

/**
 * ### IAutoOrderFiltersAndTools
 * Represents toggleable flags for various auto-order features and filters in the ordering workflow.
 *
 * @interface IAutoOrderFiltersAndTools
 * @default {@link AutoOrderFiltersAndToolsDefaults}
 * @description This interface provides a structured definition of switches used to enable or disable
 *              specific behaviors in the auto-order module, such as pack size adjustments, PO subtraction,
 *              supplier item views, and order level enforcement.
 *
 * @example This example showcases the default values.
 * const filters: IAutoOrderFiltersAndTools = {
 *   isBumpToPackSizeOn: false,
 *   isEditingPriceOn: false,
 *   isIncludeDaysZeroOn: false,
 *   isIncludeNegativeDaysOn: false,
 *   isMeetOrderLevelOn: false,
 *   isShowLineColoursOn: true,
 *   isSubtractPOOn: true,
 *   isViewSupplierItemsOn: true,
 *   isViewNonSupplierItemsOn: false,
 * };
 *
 * @extends Object
 */
export interface IAutoOrderFiltersAndTools extends Object {
  /**
   * Enables or disables bumping order quantities up to pack size.
   * @description Ensures quantities align with supplier pack sizes when enabled.
   * @type {boolean}
   * @default false
   * @example true
   */
  isBumpToPackSizeOn: boolean;

  /**
   * Allows editing of calculated prices.
   * @description Grants the user permission to modify suggested prices inline.
   * @type {boolean}
   * @default false
   * @example true
   */
  isEditingPriceOn: boolean;

  /**
   * Includes days with zero demand in calculation logic.
   * @description Affects demand averaging by accounting for days with no recorded movement.
   * @type {boolean}
   * @default false
   * @example true
   */
  isIncludeDaysZeroOn: boolean;

  /**
   * Includes days with negative stock or demand.
   * @description Useful in cases of returns or corrections where negative values are relevant.
   * @type {boolean}
   * @default false
   * @example true
   */
  isIncludeNegativeDaysOn: boolean;

  /**
   * Enforces a minimum order level before allowing placement.
   * @description Ensures the generated order meets configured thresholds.
   * @type {boolean}
   * @default false
   * @example true
   */
  isMeetOrderLevelOn: boolean;

  /**
   * Displays coloured lines for visual differentiation.
   * @description Helps users identify different line statuses or categories.
   * @type {boolean}
   * @default true
   * @example false
   */
  isShowLineColoursOn: boolean;

  /**
   * Subtracts existing purchase orders from demand or stock levels.
   * @description Prevents over-ordering by accounting for in-transit or pending POs.
   * @type {boolean}
   * @default true
   * @example false
   */
  isSubtractPOOn: boolean;

  /**
   * Shows items that are not associated with a supplier.
   * @description Expands the view to include orphaned or unlinked items.
   * @type {boolean}
   * @default false
   * @example true
   */
  isViewNonSupplierItemsOn: boolean;

  /**
   * Shows items that are associated with a supplier.
   * @description Filters the list to only display supplier-bound items when enabled.
   * @type {boolean}
   * @default true
   * @example false
   */
  isViewSupplierItemsOn: boolean;
}

//#endregion

//#region CONST AutoOrderOrderFilterAndToolDefaults
/**
 * ### AutoOrderFilterAndToolDefaults
 * Default configuration values for the `IAutoOrderFiltersAndTools` interface.
 *
 * @constant
 * @type {IAutoOrderFiltersAndTools}
 * @description Provides the predefined default state applied when initializing or resetting auto order filter and tool settings.
 *
 * @example
 * const defaults: IAutoOrderFiltersAndTools = {
 *   isBumpToPackSizeOn: false,
 *   isEditingPriceOn: false,
 *   isIncludeDaysZeroOn: false,
 *   isIncludeNegativeDaysOn: false,
 *   isMeetOrderLevelOn: false,
 *   isShowLineColoursOn: true,
 *   isSubtractPOOn: true,
 *   isViewSupplierItemsOn: true,
 *   isViewNonSupplierItemsOn: false,
 * };
 */
export const AutoOrderFiltersAndToolsDefaults: IAutoOrderFiltersAndTools = {
  isBumpToPackSizeOn: false,
  isEditingPriceOn: false,
  isIncludeDaysZeroOn: false,
  isIncludeNegativeDaysOn: false,
  isMeetOrderLevelOn: false,
  isShowLineColoursOn: true,
  isSubtractPOOn: true,
  isViewSupplierItemsOn: true,
  isViewNonSupplierItemsOn: false,
};
//#endregion
